import React from "react";
import { Grid, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { StandadCard } from "../../../../components/Cards";
import { CSS_CONSTANTS } from "../../../../constants/cssConstants";
import { api } from "../../../../api/api";

interface IAgingBucket {
  label: string;
  value: number;
  color: string;
}

interface IAccountReceivableSummary {
  pre_billed: number;
  post_dated: number;
  zero_to_thirty_days: number;
  thirty_one_to_sixty_days: number;
  sixty_one_to_ninety_days: number;
  over_ninety_days: number;
  running_balance: number;
}

export const CustomerOutstandingAging: React.FC<{
  customerId: number;
  customerCode: string;
}> = ({ customerId, customerCode }) => {
  const theme = useTheme();
  const [loading, setLoading] = React.useState(true);
  const [summaryData, setSummaryData] =
    React.useState<IAccountReceivableSummary>({
      pre_billed: 0,
      post_dated: 0,
      zero_to_thirty_days: 0,
      thirty_one_to_sixty_days: 0,
      sixty_one_to_ninety_days: 0,
      over_ninety_days: 0,
      running_balance: 0,
    });

  React.useEffect(() => {
    const fetchAgingData = async () => {
      try {
        setLoading(true);
        const response = await api.get(
          `accounting/get-account-receivable-summary-cached?code=${customerId}`
        );
        if (response.data.data && response.data.data.length > 0) {
          const record = response.data.data[0];
          setSummaryData({
            pre_billed: Number(record.pre_billed) || 0,
            post_dated: Number(record.post_dated) || 0,
            zero_to_thirty_days: Number(record.zero_to_thirty_days) || 0,
            thirty_one_to_sixty_days:
              Number(record.thirty_one_to_sixty_days) || 0,
            sixty_one_to_ninety_days:
              Number(record.sixty_one_to_ninety_days) || 0,
            over_ninety_days: Number(record.over_ninety_days) || 0,
            running_balance: Number(record.running_balance) || 0,
          });
        }
      } catch (error) {
        console.error("Failed to fetch account receivable summary:", error);
      } finally {
        setLoading(false);
      }
    };

    if (customerCode) {
      fetchAgingData();
    }
  }, [customerCode]);

  const agingData: IAgingBucket[] = [
    {
      label: "Pre-Billed",
      value: summaryData.pre_billed,
      color: theme.palette.secondary.main,
    },
    {
      label: "Post-Dated",
      value: summaryData.post_dated,
      color: "#7c4dff",
    },
    {
      label: "0-30 Days",
      value: summaryData.zero_to_thirty_days,
      color: theme.palette.success.main,
    },
    {
      label: "31-60 Days",
      value: summaryData.thirty_one_to_sixty_days,
      color: theme.palette.info.main,
    },
    {
      label: "61-90 Days",
      value: summaryData.sixty_one_to_ninety_days,
      color: theme.palette.warning.main,
    },
    {
      label: "90+ Days",
      value: summaryData.over_ninety_days,
      color: theme.palette.error.main,
    },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <StandadCard>
      {loading ? (
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6, 7].map((item) => (
            <Grid item xs={6} sm={4} md key={item}>
              <Skeleton variant="rounded" height={40} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          {agingData.map((bucket) => (
            <Grid item xs={6} sm={4} md key={bucket.label}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  p: 1.2,
                  borderRadius: CSS_CONSTANTS.BORDER_RADIUS,
                  backgroundColor: `${bucket.color}15`,
                  border: `1px solid ${bucket.color}30`,
                }}
              >
                <Typography
                  variant="body1"
                  fontSize={CSS_CONSTANTS.LABEL_VALUE_SIZE}
                  fontWeight={400}
                  color="text.secondary"
                >
                  {bucket.label}
                </Typography>
                <Typography
                  variant="body1"
                  fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
                  fontWeight={700}
                  sx={{ color: bucket.color }}
                >
                  {formatCurrency(bucket.value)}
                </Typography>
              </Stack>
            </Grid>
          ))}
          <Grid item xs={6} sm={4} md>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                p: 1.2,
                borderRadius: CSS_CONSTANTS.BORDER_RADIUS,
                backgroundColor: theme.palette.primary.light,
                border: `1px solid ${theme.palette.primary.main}30`,
              }}
            >
              <Typography
                variant="body1"
                fontSize={CSS_CONSTANTS.LABEL_VALUE_SIZE}
                fontWeight={400}
                color="text.secondary"
              >
                Total Due
              </Typography>
              <Typography
                variant="body1"
                fontSize={CSS_CONSTANTS.LABEL_HEADING_SIZE}
                fontWeight={700}
                sx={{ color: theme.palette.primary.main }}
              >
                {formatCurrency(summaryData.running_balance)}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      )}
    </StandadCard>
  );
};
